import type { CSSProperties } from 'react';

// ─── App shell ───────────────────────────────────────────────────────────────

export const appShellStyle: CSSProperties = {
  minHeight: '100vh',
  background: 'var(--color-surface, #FFF8F0)',
  color: 'var(--color-text, #2D2015)',
  fontFamily: 'var(--font-body)',
};

// ─── Header ──────────────────────────────────────────────────────────────────

export const headerStyle: CSSProperties = {
  background: 'var(--color-primary, #E8507A)',
  padding: '0.65rem 1.25rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: '0 2px 8px rgba(45,32,21,0.18)',
};

export const headerBrandStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
};

export const headerLogoStyle: CSSProperties = {
  height: 36,
  borderRadius: 4,
  objectFit: 'contain',
};

/** Org name rendered in Playfair Display, cream coloured. */
export const headerTitleStyle: CSSProperties = {
  color: '#FFF8F0',
  fontFamily: 'var(--font-heading)',
  fontWeight: 800,
  fontSize: '1.3rem',
  letterSpacing: '-0.01em',
  lineHeight: 1.1,
};

/** Small tagline beneath the org name. */
export const headerTaglineStyle: CSSProperties = {
  color: 'rgba(255,248,240,0.75)',
  fontFamily: 'var(--font-body)',
  fontSize: '0.7rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  lineHeight: 1,
  marginTop: 2,
};

export const headerNavStyle: CSSProperties = {
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'center',
};

export const navBtnStyle: CSSProperties = {
  background: 'rgba(255,248,240,0.15)',
  color: '#FFF8F0',
  border: '1.5px solid rgba(255,248,240,0.3)',
  borderRadius: 6,
  padding: '0.35rem 0.85rem',
  cursor: 'pointer',
  fontSize: '0.82rem',
  fontFamily: 'var(--font-body)',
  fontWeight: 600,
  letterSpacing: '0.02em',
  transition: 'background 0.15s',
};

export const navBtnActiveStyle: CSSProperties = {
  ...navBtnStyle,
  background: 'rgba(255,248,240,0.3)',
  border: '1.5px solid rgba(255,248,240,0.6)',
};

// ─── Page layout ─────────────────────────────────────────────────────────────

export const pageStyle: CSSProperties = {
  maxWidth: 740,
  margin: '0 auto',
  padding: '2rem 1.25rem',
};

export const headingStyle: CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontSize: '2rem',
  fontWeight: 800,
  color: 'var(--color-primary, #E8507A)',
  marginBottom: '1.5rem',
  lineHeight: 1.1,
};

export const mutedTextStyle: CSSProperties = {
  color: 'rgba(45,32,21,0.55)',
  fontFamily: 'var(--font-body)',
  padding: '2rem 0',
};

// ─── Error / feedback ────────────────────────────────────────────────────────

export const errorBoxStyle: CSSProperties = {
  background: '#fef2f2',
  border: '1.5px solid #fca5a5',
  borderRadius: 8,
  padding: '0.75rem 1rem',
  marginBottom: '1rem',
  color: '#dc2626',
  fontSize: '0.9rem',
};

export const retryBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#dc2626',
  cursor: 'pointer',
  textDecoration: 'underline',
  fontFamily: 'var(--font-body)',
};

// ─── League cards ────────────────────────────────────────────────────────────

export const cardStyle: CSSProperties = {
  background: '#fff',
  border: '1.5px solid rgba(232,80,122,0.15)',
  borderRadius: 10,
  padding: '1rem 1.25rem',
  marginBottom: '0.85rem',
  cursor: 'pointer',
  transition: 'box-shadow 0.15s, transform 0.1s',
  boxShadow: '0 2px 6px rgba(45,32,21,0.07)',
};

export const cardStaticStyle: CSSProperties = {
  ...cardStyle,
  cursor: 'default',
};

export const cardHeaderRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.75rem',
};

/** League name inside a card. */
export const cardTitleStyle: CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontWeight: 700,
  fontSize: '1.05rem',
  color: 'var(--color-text, #2D2015)',
};

export const leagueFormatTagStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-secondary, #4BAD8F)',
  background: 'rgba(75,173,143,0.12)',
  border: '1px solid rgba(75,173,143,0.3)',
  padding: '0.2rem 0.65rem',
  borderRadius: 99,
  fontWeight: 600,
  letterSpacing: '0.03em',
};

export const cardHintStyle: CSSProperties = {
  margin: '0.45rem 0 0',
  fontSize: '0.82rem',
  color: 'rgba(45,32,21,0.5)',
  fontFamily: 'var(--font-body)',
};

// ─── Match cards ─────────────────────────────────────────────────────────────

export const matchCardStyle: CSSProperties = {
  ...cardStaticStyle,
  padding: '0',
  overflow: 'hidden',
};

/** Left coloured strip on match card (holds the key tag). */
export const matchCourtStripStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.75rem 0.5rem',
  background: 'var(--color-surface, #FFF8F0)',
  borderRight: '1.5px solid rgba(232,80,122,0.12)',
  minWidth: 72,
};

export const matchBodyStyle: CSSProperties = {
  flex: 1,
  padding: '0.85rem 1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem',
};

export const matchTeamsRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  flexWrap: 'wrap',
};

export const matchTeamNameStyle: CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontWeight: 700,
  fontSize: '1rem',
  color: 'var(--color-text, #2D2015)',
};

export const matchScoreSepStyle: CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontWeight: 800,
  color: 'var(--color-primary, #E8507A)',
  fontSize: '1.1rem',
  margin: '0 0.25rem',
};

export const matchMetaRowStyle: CSSProperties = {
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
  fontSize: '0.8rem',
  color: 'rgba(45,32,21,0.55)',
};

export const backBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--color-primary, #E8507A)',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontFamily: 'var(--font-body)',
  fontWeight: 600,
  padding: 0,
  marginBottom: '1.25rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
};

// ─── Status badge helpers ────────────────────────────────────────────────────

/** Maps match / result status values to badge background colours. */
export const statusColor: Record<string, string> = {
  SCHEDULED:   '#6366f1',
  IN_PROGRESS: '#E8507A',
  COMPLETED:   '#4BAD8F',
  CANCELLED:   '#ef4444',
  PENDING:     '#C9963A',
  APPROVED:    '#4BAD8F',
  REJECTED:    '#ef4444',
};

export const badgeStyle = (color: string): import('react').CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0.15rem 0.55rem',
  borderRadius: 99,
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  background: color,
  color: '#fff',
  marginLeft: '0.35rem',
  textTransform: 'uppercase',
});


// ─── Landing page (shufflr.cloud bare domain) ────────────────────────────────

export const landingPageStyle: CSSProperties = {
  minHeight: '100vh',
  background: '#FFF8F0',
  color: '#2D2015',
  fontFamily: 'var(--font-body)',
};

export const landingHeroStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2.5rem',
  padding: '5rem 2rem 4rem',
  maxWidth: 900,
  margin: '0 auto',
  flexWrap: 'wrap',
};

export const landingTagWrapStyle: CSSProperties = {
  width: 260,
  
  flexShrink: 0,
  filter: 'drop-shadow(0 12px 32px rgba(192,64,94,0.28))',
};

export const landingHeadlineStyle: CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontSize: 'clamp(1.6rem, 4vw, 2.4rem)',
  fontWeight: 800,
  color: '#E8507A',
  lineHeight: 1.1,
  margin: '0 0 1.1rem',
};

export const landingSubStyle: CSSProperties = {
  fontSize: '1.05rem',
  lineHeight: 1.65,
  color: 'rgba(45,32,21,0.72)',
  maxWidth: 440,
  margin: '0 0 1.75rem',
  fontFamily: 'var(--font-body)',
};

export const landingCtaRowStyle: CSSProperties = {
  marginBottom: '1.25rem',
};

export const landingCtaBtnStyle: CSSProperties = {
  background: '#E8507A',
  color: '#FFF8F0',
  border: 'none',
  borderRadius: 8,
  padding: '0.65rem 1.4rem',
  fontFamily: 'var(--font-body)',
  fontWeight: 700,
  fontSize: '0.95rem',
  cursor: 'pointer',
  letterSpacing: '0.02em',
  transition: 'background 0.15s',
  whiteSpace: 'nowrap',
};

export const landingDemoLinkStyle: CSSProperties = {
  color: '#4BAD8F',
  fontFamily: 'var(--font-body)',
  fontWeight: 600,
  fontSize: '0.9rem',
  textDecoration: 'none',
  letterSpacing: '0.01em',
};

export const landingDividerStyle: CSSProperties = {
  width: '100%',
  height: 1,
  background: 'rgba(232,80,122,0.12)',
  margin: '0 auto',
  maxWidth: 860,
};

export const landingFeaturesStyle: CSSProperties = {
  maxWidth: 900,
  margin: '0 auto',
  padding: '4rem 2rem',
};

export const landingFeatureCardStyle: CSSProperties = {
  background: '#fff',
  border: '1.5px solid rgba(232,80,122,0.12)',
  borderRadius: 12,
  padding: '1.5rem',
  boxShadow: '0 2px 10px rgba(45,32,21,0.06)',
};

export const landingFeatureIconStyle: CSSProperties = {
  fontSize: '1.8rem',
  display: 'block',
  marginBottom: '0.75rem',
};

export const landingFeatureTitleStyle: CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontWeight: 700,
  fontSize: '1rem',
  color: '#2D2015',
  margin: '0 0 0.5rem',
};

export const landingFeatureTextStyle: CSSProperties = {
  fontSize: '0.88rem',
  lineHeight: 1.6,
  color: 'rgba(45,32,21,0.65)',
  margin: 0,
  fontFamily: 'var(--font-body)',
};

export const landingQuoteStyle: CSSProperties = {
  maxWidth: 520,
  margin: '0 auto 4rem',
  textAlign: 'center',
  fontFamily: 'var(--font-accent,"Pacifico",cursive)',
  fontSize: '1.15rem',
  color: '#E8507A',
  padding: '0 2rem',
  borderLeft: 'none',
  opacity: 0.85,
};

export const landingFooterStyle: CSSProperties = {
  textAlign: 'center',
  padding: '2rem',
  borderTop: '1px solid rgba(232,80,122,0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  flexWrap: 'wrap',
  color: '#2D2015',
};

// ─── Auth pages ───────────────────────────────────────────────────────────────

export const authPageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#FFF8F0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px 16px',
  fontFamily: "'DM Sans', Arial, sans-serif",
};

export const authCardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 20,
  boxShadow: '0 4px 32px rgba(232,133,122,0.10)',
  padding: '40px 36px',
  width: '100%',
  maxWidth: 420,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0,
};

export const authLogoStyle: React.CSSProperties = {
  height: 36,
  width: 'auto',
  marginBottom: 8,
};

export const authTaglineStyle: React.CSSProperties = {
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontSize: 11,
  letterSpacing: 2,
  textTransform: 'uppercase',
  color: '#4BAD8F',
  marginBottom: 28,
};

export const authHeadingStyle: React.CSSProperties = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: 26,
  fontWeight: 700,
  color: '#2D2015',
  marginBottom: 24,
  textAlign: 'center',
};

export const authFormStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  width: '100%',
};

export const authRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  width: '100%',
};

export const authLabelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  fontSize: 13,
  fontWeight: 600,
  color: '#2D2015',
  flex: 1,
};

export const authInputStyle: React.CSSProperties = {
  padding: '11px 14px',
  borderRadius: 10,
  border: '1.5px solid #e8d5c8',
  fontSize: 15,
  fontFamily: "'DM Sans', Arial, sans-serif",
  color: '#2D2015',
  background: '#FFF8F0',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

export const authSubmitStyle: React.CSSProperties = {
  marginTop: 6,
  padding: '13px',
  borderRadius: 12,
  border: 'none',
  background: '#E8507A',
  color: '#FFF8F0',
  fontSize: 16,
  fontWeight: 700,
  fontFamily: "'DM Sans', Arial, sans-serif",
  cursor: 'pointer',
  letterSpacing: 0.5,
  transition: 'background 0.15s',
};

export const authSwitchStyle: React.CSSProperties = {
  marginTop: 18,
  fontSize: 14,
  color: '#6b7280',
  textAlign: 'center',
};

export const authLinkStyle: React.CSSProperties = {
  color: '#E8507A',
  fontWeight: 600,
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  fontSize: 'inherit',
  fontFamily: 'inherit',
  padding: 0,
  textDecoration: 'underline',
};

export const authErrorStyle: React.CSSProperties = {
  background: '#fde8e8',
  border: '1px solid #f5a0a0',
  borderRadius: 10,
  padding: '10px 14px',
  color: '#b91c1c',
  fontSize: 13,
  width: '100%',
  boxSizing: 'border-box',
};

export const authHintStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#9ca3af',
  marginTop: 2,
};

export const headerUserStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#FFF8F0',
  opacity: 0.85,
  fontFamily: "'DM Sans', Arial, sans-serif",
};

export const navLogoutStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 20,
  border: '1.5px solid rgba(255,248,240,0.5)',
  background: 'transparent',
  color: '#FFF8F0',
  fontSize: 13,
  fontFamily: "'DM Sans', Arial, sans-serif",
  cursor: 'pointer',
  fontWeight: 600,
};

export const scoreActionWrapStyle: React.CSSProperties = {
  borderTop: '1px solid rgba(74,186,143,0.2)',
  paddingTop: 14,
  marginTop: 10,
};

export const submitScoreBtnStyle: React.CSSProperties = {
  background: '#E8507A',
  color: '#FFF8F0',
  border: 'none',
  borderRadius: 20,
  padding: '7px 18px',
  fontSize: 13,
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontWeight: 600,
  cursor: 'pointer',
  letterSpacing: '0.02em',
};

export const scoreFormRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  marginTop: 8,
};

export const scoreInputStyle: React.CSSProperties = {
  width: 58,
  padding: '6px 8px',
  borderRadius: 8,
  border: '1.5px solid #4BAD8F',
  background: '#FFF8F0',
  color: '#2D2015',
  fontSize: 15,
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontWeight: 700,
  textAlign: 'center',
};

export const scoreInputLabelStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#4BAD8F',
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

export const scoreVsSepStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#9ca3af',
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontWeight: 700,
};

export const approveScoreBtnStyle: React.CSSProperties = {
  background: '#4BAD8F',
  color: '#FFF8F0',
  border: 'none',
  borderRadius: 20,
  padding: '7px 18px',
  fontSize: 13,
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontWeight: 600,
  cursor: 'pointer',
};

export const rejectScoreBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#E8507A',
  border: '1.5px solid #E8507A',
  borderRadius: 20,
  padding: '6px 16px',
  fontSize: 13,
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontWeight: 600,
  cursor: 'pointer',
};

export const scoreActionErrorStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#b91c1c',
  marginTop: 4,
  fontFamily: "'DM Sans', Arial, sans-serif",
};

export const pendingBannerStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#92400e',
  background: '#fef3c7',
  borderRadius: 8,
  padding: '4px 10px',
  marginTop: 4,
  display: 'inline-block',
  fontFamily: "'DM Sans', Arial, sans-serif",
};

// ─── Admin dashboard ─────────────────────────────────────────────────────────

export const adminTabRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.4rem',
  flexWrap: 'wrap',
  borderBottom: '2px solid rgba(232,80,122,0.15)',
  paddingBottom: '0.5rem',
  marginBottom: '0.25rem',
};

export const adminTabBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1.5px solid rgba(232,80,122,0.25)',
  borderRadius: 6,
  padding: '0.35rem 0.9rem',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontWeight: 600,
  color: 'rgba(45,32,21,0.65)',
  transition: 'all 0.15s',
};

export const adminTabBtnActiveStyle: React.CSSProperties = {
  ...adminTabBtnStyle,
  background: '#E8507A',
  borderColor: '#E8507A',
  color: '#FFF8F0',
};

export const adminSectionHeadStyle: React.CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontSize: '1.25rem',
  fontWeight: 700,
  color: 'var(--color-text, #2D2015)',
  margin: 0,
};

export const adminToolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '0.75rem',
  marginBottom: '1rem',
};

export const adminPrimaryBtnStyle: React.CSSProperties = {
  background: '#E8507A',
  color: '#FFF8F0',
  border: 'none',
  borderRadius: 8,
  padding: '0.4rem 1rem',
  fontSize: '0.85rem',
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontWeight: 700,
  cursor: 'pointer',
  letterSpacing: '0.01em',
  transition: 'background 0.15s',
};

export const adminSecondaryBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#E8507A',
  border: '1.5px solid #E8507A',
  borderRadius: 8,
  padding: '0.35rem 0.85rem',
  fontSize: '0.82rem',
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.15s',
};

export const adminDangerBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#dc2626',
  border: '1.5px solid #dc2626',
  borderRadius: 8,
  padding: '0.3rem 0.75rem',
  fontSize: '0.8rem',
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
};

export const adminFormGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.85rem',
};

export const adminFormRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.75rem',
  flexWrap: 'wrap',
};

export const adminLabelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.3rem',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: '#2D2015',
  flex: 1,
};

export const adminInputStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: 8,
  border: '1.5px solid #e8d5c8',
  fontSize: '0.9rem',
  fontFamily: "'DM Sans', Arial, sans-serif",
  color: '#2D2015',
  background: '#FFF8F0',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

export const adminSelectStyle: React.CSSProperties = {
  ...adminInputStyle,
  cursor: 'pointer',
};

export const adminModalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(45,32,21,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem',
};

export const adminModalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 14,
  padding: '1.75rem',
  width: '100%',
  maxWidth: 480,
  boxShadow: '0 8px 40px rgba(45,32,21,0.18)',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.25rem',
};

export const adminModalTitleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#2D2015',
  margin: 0,
};

export const adminModalFootStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.6rem',
  alignItems: 'center',
  flexWrap: 'wrap',
  paddingTop: '0.5rem',
  borderTop: '1px solid rgba(232,80,122,0.1)',
};

export const adminEmptyStyle: React.CSSProperties = {
  color: 'rgba(45,32,21,0.4)',
  fontFamily: 'var(--font-body)',
  fontSize: '0.9rem',
  padding: '1.5rem 0',
  fontStyle: 'italic',
};

export const adminChipStyle: React.CSSProperties = {
  background: 'rgba(75,173,143,0.12)',
  border: '1px solid rgba(75,173,143,0.3)',
  borderRadius: 99,
  padding: '0.15rem 0.6rem',
  fontSize: '0.78rem',
  color: '#2D6E57',
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontWeight: 600,
};

// ─── Standings table ──────────────────────────────────────────────────────────

export const standingsTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: "'DM Sans', Arial, sans-serif",
  fontSize: '0.88rem',
  background: '#fff',
  borderRadius: 10,
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(45,32,21,0.07)',
};

export const standingsThStyle: React.CSSProperties = {
  padding: '0.65rem 0.75rem',
  background: 'var(--color-primary, #E8507A)',
  color: '#FFF8F0',
  fontWeight: 700,
  fontSize: '0.78rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  textAlign: 'center',
  whiteSpace: 'nowrap',
};

export const standingsTdStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  textAlign: 'center',
  borderBottom: '1px solid rgba(232,80,122,0.08)',
  color: '#2D2015',
};

export const standingsRankCellStyle: React.CSSProperties = {
  ...standingsTdStyle,
  fontWeight: 800,
  fontSize: '1rem',
  color: 'var(--color-primary, #E8507A)',
  width: 36,
};

export const standingsWinPctStyle: React.CSSProperties = {
  ...standingsTdStyle,
  fontWeight: 700,
  color: 'var(--color-secondary, #4BAD8F)',
};

export const standingsRowStyle: React.CSSProperties = {};

export const standingsRowTopStyle: React.CSSProperties = {
  background: 'rgba(232,80,122,0.03)',
};

// ─── Bracket styles ───────────────────────────────────────────────────────────

export const bracketWrapStyle: CSSProperties = {
  display: 'flex',
  gap: 0,
  overflowX: 'auto',
  paddingBottom: 16,
  alignItems: 'flex-start',
};

export const bracketRoundColStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  minWidth: 180,
  flex: '0 0 auto',
  paddingRight: 24,
  position: 'relative',
};

export const bracketRoundTitleStyle: CSSProperties = {
  fontWeight: 700,
  fontSize: 13,
  color: '#E8507A',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  paddingBottom: 8,
  borderBottom: '2px solid #fce7ef',
  marginBottom: 4,
};

export const bracketMatchCardStyle: CSSProperties = {
  background: '#fff',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  padding: '10px 12px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};

export const bracketTeamRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 0',
  borderBottom: '1px solid #f3f4f6',
};

export const bracketTeamNameStyle: CSSProperties = {
  fontSize: 13,
  color: '#374151',
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 110,
};

export const bracketScoreStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: '#111827',
  minWidth: 24,
  textAlign: 'right',
};

export const bracketWinnerStyle: CSSProperties = {
  color: '#10b981',
  fontWeight: 700,
};

export const bracketByeStyle: CSSProperties = {
  fontSize: 12,
  color: '#9ca3af',
  fontStyle: 'italic',
  padding: '8px 0',
  textAlign: 'center',
};

export const bracketSeedBtnStyle: CSSProperties = {
  background: '#E8507A',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  marginTop: 6,
};

export const bracketSeedFormStyle: CSSProperties = {
  background: '#fdf6ee',
  border: '1px solid #fce7ef',
  borderRadius: 10,
  padding: '14px 16px',
};

export const bracketSeedRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
};

export const bracketSeedLabelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  width: 70,
  flexShrink: 0,
  color: '#374151',
};

export const bracketSeedInputStyle: CSSProperties = {
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '4px 8px',
  fontSize: 13,
  width: 80,
};

export const bracketSeedSelectStyle: CSSProperties = {
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '4px 8px',
  fontSize: 13,
  background: '#fff',
};

export const bracketStatusBadgeStyle = (color: string): CSSProperties => ({
  background: color + '22',
  color: color,
  borderRadius: 20,
  padding: '2px 10px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
});
